/**
 * useClickTracking Hook
 *
 * Attaches a single delegated click listener to `document`.
 * On click, walks up the DOM tree looking for `data-track` attributes
 * and matches them against the `clickEvents` config.
 *
 * Also reads `data-track-*` attributes as extra event properties.
 * Example: `data-track-project-id="123"` → `{ project_id: "123" }`.
 *
 * Place this hook once in the root App component.
 *
 * @module @ideoz/core/analytics/hooks/useClickTracking
 */

import { useEffect } from 'react';
import { analyticsConfig } from '../analytics.config';
import { trackEvent } from '../analytics.service';

/**
 * Convert data-track-* attribute names to property keys.
 * e.g. "data-track-project-id" → "project_id"
 */
function attrToKey(attr: string): string {
  // Remove "data-track-" prefix, then convert kebab-case to snake_case
  return attr
    .replace(/^data-track-/, '')
    .replace(/-/g, '_');
}

/**
 * Extract all `data-track-*` properties from an element.
 */
function extractTrackProperties(element: HTMLElement): Record<string, string> {
  const props: Record<string, string> = {};

  for (const attr of Array.from(element.attributes)) {
    if (attr.name.startsWith('data-track-') && attr.name !== 'data-track') {
      props[attrToKey(attr.name)] = attr.value;
    }
  }

  return props;
}

/**
 * Click tracking hook.
 * Call once in the root component to enable declarative click tracking.
 */
export function useClickTracking(): void {
  useEffect(() => {
    function handleClick(event: MouseEvent): void {
      try {
        let target = event.target as HTMLElement | null;

        // Walk up the DOM tree looking for data-track
        while (target && target !== document.documentElement) {
          const trackValue = target.getAttribute('data-track');

          if (trackValue) {
            const mapping = analyticsConfig.clickEvents[trackValue];
            if (mapping) {
              const extraProps = extractTrackProperties(target);
              trackEvent(mapping.event_name, extraProps);
            }
            return; // Stop after first match (don't double-fire for nested elements)
          }

          target = target.parentElement;
        }
      } catch (error) {
        console.warn('Analytics click tracking error (non-blocking):', error);
      }
    }

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, []);
}
